import { formatDistanceToNow, format, isValid, parseISO } from 'date-fns';

/**
 * Format a date as a relative time string (e.g., "2 hours ago", "3 days ago")
 * Falls back to absolute date for very old dates (> 7 days)
 *
 * @param {string|Date} dateInput - The date to format
 * @param {Object} options - Options
 * @param {boolean} options.addSuffix - Whether to add "ago" suffix (default: true)
 * @param {number} options.absoluteThresholdDays - Days after which to show absolute date (default: 7)
 * @returns {string} - Formatted date string
 */
export function formatRelativeDate(dateInput, options = {}) {
  const { addSuffix = true, absoluteThresholdDays = 7 } = options;

  if (!dateInput) {
    return 'Never';
  }

  // Parse the date
  let date;
  if (typeof dateInput === 'string') {
    date = parseISO(dateInput);
  } else {
    date = dateInput;
  }

  // Validate the date
  if (!isValid(date)) {
    return 'Invalid date';
  }

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  // For very recent times (less than 1 minute ago)
  const diffInSeconds = diffInMs / 1000;
  if (diffInSeconds < 60) {
    return 'Just now';
  }

  // For dates older than the threshold, show absolute date
  if (diffInDays > absoluteThresholdDays) {
    return format(date, 'MMM d, yyyy');
  }

  // For recent dates, show relative time
  return formatDistanceToNow(date, { addSuffix });
}

/**
 * Format a date for display with both relative and absolute information
 * Returns the relative time with the absolute date as a title attribute
 *
 * @param {string|Date} dateInput - The date to format
 * @returns {Object} - { relative: string, absolute: string }
 */
export function formatDateWithTooltip(dateInput) {
  if (!dateInput) {
    return { relative: 'Never', absolute: '' };
  }

  let date;
  if (typeof dateInput === 'string') {
    date = parseISO(dateInput);
  } else {
    date = dateInput;
  }

  if (!isValid(date)) {
    return { relative: 'Invalid date', absolute: '' };
  }

  return {
    relative: formatRelativeDate(date),
    absolute: format(date, 'PPpp'), // e.g., "Feb 4, 2026, 10:30 PM"
  };
}

/**
 * Format a date as an absolute date string
 *
 * @param {string|Date} dateInput - The date to format
 * @param {string} formatStr - The date-fns format string (default: 'MMM d, yyyy')
 * @returns {string} - Formatted date string
 */
export function formatAbsoluteDate(dateInput, formatStr = 'MMM d, yyyy') {
  if (!dateInput) {
    return 'Never';
  }

  let date;
  if (typeof dateInput === 'string') {
    date = parseISO(dateInput);
  } else {
    date = dateInput;
  }

  if (!isValid(date)) {
    return 'Invalid date';
  }

  return format(date, formatStr);
}
